import { Skeleton } from '@/shared/components/ui/skeleton';
import { cn } from '@/shared/utils/utils';
import type { ReactNode } from 'react';

/**
 * Reusable skeleton primitives for building consistent loading states.
 * These components extract common patterns used across skeleton components.
 */

/**
 * Skeleton search bar with icon placeholder.
 * Used in admin tables and booking lists.
 */
export function SkeletonSearchBar({ className }: { className?: string }) {
  return (
    <div className={cn('relative flex-1', className)}>
      <Skeleton className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 rounded" />
      <Skeleton className="h-10 w-full rounded-md" />
    </div>
  );
}

/**
 * Skeleton pagination controls with page count and navigation buttons.
 */
export function SkeletonPagination({ className }: { className?: string }) {
  return (
    <div className={cn('flex items-center justify-between', className)}>
      <Skeleton className="h-4 w-28" />
      <div className="flex gap-1">
        <Skeleton className="h-8 w-8 rounded" />
        <Skeleton className="h-8 w-8 rounded" />
        <Skeleton className="h-8 w-8 rounded" />
      </div>
    </div>
  );
}

/**
 * Standard card container with consistent styling.
 * Used as wrapper for content sections.
 */
export function SkeletonCard({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        'bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700/50 p-4 shadow-sm',
        className
      )}
    >
      {children}
    </div>
  );
}

/**
 * Single booking row skeleton.
 * Matches the UnifiedBookingList row structure.
 */
export function SkeletonBookingRow() {
  return (
    <div className="border-b border-gray-100 dark:border-slate-700/50 p-4 last:border-b-0">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          {/* Circular icon */}
          <Skeleton className="h-10 w-10 rounded-full flex-shrink-0" />
          <div className="space-y-1 min-w-0">
            {/* Name + Badge row */}
            <div className="flex items-center gap-2">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-5 w-14 rounded" />
            </div>
            {/* Date */}
            <Skeleton className="h-4 w-28" />
            {/* Time */}
            <Skeleton className="h-3 w-16" />
          </div>
        </div>
        {/* Action button */}
        <Skeleton className="h-8 w-8 rounded flex-shrink-0" />
      </div>
    </div>
  );
}

/**
 * List of booking rows with container.
 * @param count - Number of rows to display (default: 5)
 */
export function SkeletonBookingList({ count = 5 }: { count?: number }) {
  return (
    <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700/50 overflow-hidden shadow-sm">
      {Array.from({ length: count }).map((_, i) => (
        <SkeletonBookingRow key={i} />
      ))}
    </div>
  );
}

/**
 * Profile header skeleton matching AdminHeader with blue variant.
 * Used in ProfilePageSkeleton.
 */
export function SkeletonProfileHeader() {
  return (
    <div className="relative w-full overflow-hidden rounded-2xl border border-blue-100 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-8 dark:border-blue-900 dark:from-blue-950/30 dark:via-indigo-950/30 dark:to-purple-950/30">
      {/* Decorative circle */}
      <div className="absolute top-0 right-0 transform translate-x-16 -translate-y-16">
        <div className="w-64 h-64 rounded-full bg-gradient-to-br from-blue-200/20 to-purple-200/20" />
      </div>

      {/* Mobile settings */}
      <Skeleton className="absolute top-4 right-4 z-10 size-9 rounded-md md:hidden" />

      {/* Content - matches profile header structure */}
      <div className="relative">
        <div className="flex items-center justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-4">
              <Skeleton className="size-16 sm:size-20 shrink-0 rounded-full border-2 border-white dark:border-slate-800 sm:border-4" />
              <div className="min-w-0 space-y-1">
                <Skeleton className="h-8 w-40 sm:h-9 sm:w-48" />
                <Skeleton className="h-4 w-48 sm:h-5 sm:w-56" />
              </div>
            </div>
          </div>
          {/* Desktop: settings + office selector */}
          <div className="hidden md:flex items-center gap-3 shrink-0 flex-row-reverse">
            <Skeleton className="size-9 rounded-md" />
            <Skeleton className="h-10 w-64 rounded-md" />
          </div>
        </div>
        {/* Mobile office selector */}
        <div className="md:hidden mt-4 w-full">
          <Skeleton className="h-10 w-full rounded-md" />
        </div>
      </div>
    </div>
  );
}
